import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Stack,
  Alert,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { EmailTemplate, UserEmailTemplateOverride } from '../../services/emailTemplate.service';

interface EmailTemplateEditorProps {
  template: EmailTemplate | null;
  userOverride?: UserEmailTemplateOverride;
  onSave: (data: Partial<EmailTemplate>) => void;
  onSaveUserOverride: (templateId: number, data: any) => void;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`editor-tabpanel-${index}`}
      aria-labelledby={`editor-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const EmailTemplateEditor: React.FC<EmailTemplateEditorProps> = ({
  template,
  userOverride,
  onSave,
  onSaveUserOverride,
  onClose,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    description: '',
    markdown_body: '',
    is_active: true,
  });
  const [userOverrideData, setUserOverrideData] = useState({
    subject: '',
    markdown_body: '',
    is_active: true,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name,
        subject: template.subject,
        description: template.description || '',
        markdown_body: template.markdown_body || '',
        is_active: template.is_active,
      });
    }

    if (userOverride) {
      setUserOverrideData({
        subject: userOverride.subject || '',
        markdown_body: userOverride.markdown_body || '',
        is_active: userOverride.is_active,
      });
    }
  }, [template, userOverride]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleUserOverrideChange = (field: string, value: any) => {
    setUserOverrideData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Template name is required';
    }
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.markdown_body.trim()) {
      newErrors.markdown_body = 'Template content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (tabValue === 0) {
        // Save global template
        await onSave(formData);
      } else {
        // Save user override
        if (template) {
          await onSaveUserOverride(template.id, userOverrideData);
        }
      }
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const getVariableChips = () => {
    if (!template?.variables) return null;

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Available Variables:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {template.variables.map((variable, index) => (
            <Chip
              key={index}
              label={`{{ $${variable} }}`}
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => {
                // Insert variable into the current textarea
                const currentContent = tabValue === 0 ? formData.markdown_body : userOverrideData.markdown_body;
                const newContent = currentContent + `{{ $${variable} }}`;
                
                if (tabValue === 0) {
                  handleInputChange('markdown_body', newContent);
                } else {
                  handleUserOverrideChange('markdown_body', newContent);
                }
              }}
              sx={{ cursor: 'pointer', mb: 1 }}
            />
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="between" alignItems="center">
          <Typography variant="h6">
            {template ? `Edit Template: ${template.name}` : 'Create New Template'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="template editor tabs">
            <Tab label="Global Template" id="editor-tab-0" />
            {template && <Tab label="User Override" id="editor-tab-1" />}
          </Tabs>
        </Box>

        {/* Global Template Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <TextField
                label="Template Name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name || (template ? 'System template key — cannot be changed' : undefined)}
                fullWidth
                required
                disabled={!!template}
              />

              <TextField
                label="Subject"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                error={!!errors.subject}
                helperText={errors.subject}
                fullWidth
                required
              />

              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                fullWidth
                multiline
                rows={2}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  />
                }
                label="Active"
              />

              <Divider />
              
              {getVariableChips()}

              <TextField
                label="Template Content (Markdown)"
                value={formData.markdown_body}
                onChange={(e) => handleInputChange('markdown_body', e.target.value)}
                error={!!errors.markdown_body}
                helperText={errors.markdown_body || 'Use Markdown syntax. Click variables above to insert them.'}
                fullWidth
                required
                multiline
                rows={12}
                variant="outlined"
              />
            </Stack>
          </Box>
        </TabPanel>

        {/* User Override Tab */}
        {template && (
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                User overrides allow you to customize this template for your account without affecting the global template.
              </Alert>

              <Stack spacing={3}>
                <TextField
                  label="Subject Override"
                  value={userOverrideData.subject}
                  onChange={(e) => handleUserOverrideChange('subject', e.target.value)}
                  fullWidth
                  placeholder={template.subject}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={userOverrideData.is_active}
                      onChange={(e) => handleUserOverrideChange('is_active', e.target.checked)}
                    />
                  }
                  label="Active Override"
                />

                <Divider />
                
                {getVariableChips()}

                <TextField
                  label="Content Override (Markdown)"
                  value={userOverrideData.markdown_body}
                  onChange={(e) => handleUserOverrideChange('markdown_body', e.target.value)}
                  fullWidth
                  multiline
                  rows={12}
                  variant="outlined"
                  placeholder={template.markdown_body}
                  helperText="Leave empty to use the global template content. Use Markdown syntax."
                />
              </Stack>
            </Box>
          </TabPanel>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'Save Template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTemplateEditor;